import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Hard-coded prompt hints for the baked-in agents. Custom agents get their hint
// from the catalog response.
const HINTS = {
  "invoice-create": 'e.g. "Create AP invoice for supplier ABC Ltd, $5000, NET30"',
  "supplier-create": 'e.g. "Create supplier Acme Inc, USA, payment terms NET45"',
  "invoice-ageing": 'e.g. "Show invoices overdue more than 30 days"',
  "supplier-ageing": 'e.g. "List top 10 suppliers by outstanding balance"',
  "journal-create": 'e.g. "Post journal: Debit Cash $1000, Credit Sales $1000"',
  "receipt-create": 'e.g. "Apply $500 receipt to customer XYZ invoice INV-001"',
  "customer-create": 'e.g. "Create customer Globex Corp, billing USD, NET30"',
  "get-hr": 'e.g. "List active employees in Finance department"',
  "create-hr": 'e.g. "Create employee John Doe, Accountant, joining 2026-07-01"',
  "purchase-req-create": 'e.g. "Raise a requisition for 10 laptops, $1200 each, deliver to HQ by next month"',
  "purchase-order-create": 'e.g. "Create a PO to supplier Acme for 100 units of item A at $25, ship to Dubai warehouse"',
  "rfp-create": 'e.g. "Create an RFP for office furniture, invite suppliers Acme and Globex, close in 14 days"',
  "bank-transfer": 'e.g. "Transfer $50,000 from Corporate USD account to Operations USD account today"',
  "bank-balance": 'e.g. "Show the latest balance of the Corporate USD bank account"',
  "asset-addition": 'e.g. "Add a new laptop asset, cost $1500, in service today, category Computer-PC, location San Jose"',
  "asset-transfer": 'e.g. "Transfer asset FA-1001 from San Jose to Dubai office"',
  "asset-reconcile": 'e.g. "Reconcile asset FA-1001 after physical count: 2 units found at Dubai office"',
  "sales-order-booking": 'e.g. "Book a sales order for ABC Corp: 10 units of AS54888 at $250, ship 2026-05-01, book immediately"',
  "finance-account-overview": 'e.g. "Show Apr-26 trial balance for Vision Operations ledger — opening, activity, closing per account"',
  "finance-anomaly": 'e.g. "Find unusual journals in Apr-26 over $100K and drill to AP / AR root causes"',
  "finance-variance": 'e.g. "Compare Apr-26 actuals vs budget for Vision Operations and flag variances over 10%"',
  "finance-future-insight": 'e.g. "Forecast Q3-26 revenue accounts (4xxx) based on the last 6 months of activity"',
  "finance-narrative": 'e.g. "Generate Apr-26 MIS commentary for Vision Operations and email it to cfo@example.com"',
  "finance-monthend": 'e.g. "Check Apr-26 close readiness for Vision Operations across GL, AP, AR, FA"',
};

const ErpContext = createContext(null);

export function ErpProvider({ children }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [agents, setAgents] = useState([]);        // dynamic catalog: [{key, label, category, hint, is_custom}]
  const [openAgent, setOpenAgent] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [pendingAgent, setPendingAgent] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/erp-agents/my-profile");
      setProfile(data);
      return data;
    } catch {
      setProfile({ configured: false });
      return { configured: false };
    }
  }, []);

  const fetchCatalog = useCallback(async () => {
    try {
      const { data } = await api.get("/erp-agents/catalog");
      const flat = [];
      for (const cat of ["core", "finance_efficiency"]) {
        for (const a of data[cat] || []) {
          flat.push({
            key: a.key,
            label: a.name,
            category: a.category,
            hint: a.hint || HINTS[a.key] || "",
            is_custom: !!a.is_custom,
          });
        }
      }
      setAgents(flat);
      return flat;
    } catch {
      setAgents([]);
      return [];
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchCatalog();
    } else {
      setProfile(null);
      setAgents([]);
    }
  }, [user, fetchProfile, fetchCatalog]);

  const launchAgent = useCallback((key) => {
    if (profile && profile.configured) {
      setOpenAgent(key);
    } else {
      setPendingAgent(key);
      setSetupOpen(true);
    }
  }, [profile]);

  const onProfileSaved = useCallback(async () => {
    setSetupOpen(false);
    const fresh = await fetchProfile();
    if (pendingAgent && fresh.configured) {
      setOpenAgent(pendingAgent);
      setPendingAgent(null);
    }
  }, [pendingAgent, fetchProfile]);

  const value = {
    profile, fetchProfile,
    agents, fetchCatalog,
    openAgent, setOpenAgent,
    setupOpen, setSetupOpen,
    pendingAgent, setPendingAgent,
    launchAgent, onProfileSaved,
  };
  return <ErpContext.Provider value={value}>{children}</ErpContext.Provider>;
}

export const useErp = () => useContext(ErpContext);

// Back-compat: a static AGENTS list still consumed by DashboardPage for label lookup.
// New code should read from useErp().agents instead.
export const AGENTS = Object.entries(HINTS).map(([key, hint]) => ({
  key,
  hint,
  category: key.startsWith("finance-") ? "finance_efficiency" : "core",
  label: key.split("-").map(w => w[0].toUpperCase() + w.slice(1)).join(" "),
}));

